/**
 * NX-2783 — shared display formatters.
 *
 * Every figure on screen is meant to be readable by a delivery lead, not a machine. Three places
 * were shipping raw source values straight into the DOM: Assessment rendered `submitted
 * 2026-06-21T00:00:00Z`, Connectors rendered `updated 2026-06-12T10:05:00.000Z`, and Connectors'
 * coverage table rendered ingest lag as bare seconds (fine at the 45s fixture, unreadable at the
 * `784959s` a genuinely stale connector produces). NX-2713 shipped the same class of defect to
 * production once already — a raw `21.41274883101852 days`.
 *
 * The conversions themselves already existed, duplicated per file: `day()` in EvidenceContent,
 * `DATE_FMT`/`formatDate` in RoadmapSwimlane, `DAY_SECONDS`/`HOUR_SECONDS` + `decimal` in
 * ProjectAnalyticsPanels. This module is the shared home so a fourth screen does not invent a
 * fourth style. Existing callers are deliberately NOT refactored here — that is churn on screens
 * that already render correctly, and this change is landing days before a customer demo.
 */

const MINUTE_SECONDS = 60;
const HOUR_SECONDS = 3_600;
const DAY_SECONDS = 86_400;

// UTC, deliberately. These are source-of-truth instants: an assessment submitted at
// 2026-06-21T00:00:00Z must not read as "Jun 20" to a viewer in UTC-4, because the figure would
// then disagree with the ticket, the event log and every other system that quotes the same
// timestamp. It also keeps rendering deterministic — CI runs in UTC and laptops do not, so a
// locale-local formatter makes the same test pass on one machine and fail on the other.
const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

const decimal = (value: number, places = 1): string => String(Number(value.toFixed(places)));

/**
 * An ISO timestamp as a short, human date ("Jun 21, 2026").
 *
 * A value that does not parse is returned VERBATIM rather than rendered as "Invalid Date": if the
 * source handed us something unexpected, showing it unchanged is honest and debuggable, whereas
 * "Invalid Date" destroys the evidence and tells the reader nothing.
 */
export function formatDate(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === "") return "";
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : DATE_FMT.format(parsed);
}

/**
 * A duration in seconds as a human string, scaled to the largest sensible unit:
 * `45s` / `12m` / `3.4h` / `9.1d`. Sub-minute values keep whole seconds because a connector lagging
 * "0.8m" reads worse than "45s".
 *
 * Negative input is clamped to 0: a negative duration is not a real measurement, and rendering
 * "-3.2h of lag" would be a figure the system cannot justify.
 */
export function formatDurationSeconds(seconds: number): string {
  if (!Number.isFinite(seconds)) return "—";
  const s = Math.max(0, seconds);
  if (s < MINUTE_SECONDS) return `${Math.round(s)}s`;
  if (s < HOUR_SECONDS) return `${Math.round(s / MINUTE_SECONDS)}m`;
  if (s < DAY_SECONDS) return `${decimal(s / HOUR_SECONDS)}h`;
  return `${decimal(s / DAY_SECONDS)}d`;
}

/**
 * NX-2806 — client-facing copy must never leak snake_case identifiers. The API's reason strings
 * carry dimension ids verbatim ("worst observed dimension: change_recency (...)"); this joins the
 * tokens with spaces and sentence-cases the result. Purely presentational — the API contract and
 * screen-reader text keep the exact reason semantics.
 */
export function humanizeReason(reason: string): string {
  const spaced = reason.replace(/\b[a-z0-9]+(?:_[a-z0-9]+)+\b/g, (t) => t.replaceAll("_", " "));
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
